import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useReservationBillingOptions } from "@/hooks/useReservationBillingOptions";
import { useStayDailyRates } from "@/hooks/useStayDailyRates";
import { useContractStayPlan } from "../plans/useContractStayPlan";
import { StayPlanContractDialog } from "../plans/StayPlanContractDialog";
import type { StayPlanContractDraft } from "../plans/stayPlanContractDraft";
import { StayCoverageChoice } from "../reservation/StayCoverageChoice";
import { useStayReservationCoverage } from "./useStayReservationCoverage";
import { useSwapStayReservationCoverage } from "./useSwapStayReservationCoverage";
import type { StayCoverageTarget } from "./stayReservationCoverage";
import type { StayPlanModule } from "@/lib/daycare-hotel/stayPlans";

export function StayCoverageEditor({ reservationId, petId, ownerId, module, checkInDate }: { reservationId: string; petId: string; ownerId: string | null; module: StayPlanModule; checkInDate: string }) {
  const { t } = useTranslation("daycareHotel");
  const coverage = useStayReservationCoverage({ reservationId, petId, module });
  const options = useReservationBillingOptions({ module, petId, checkInDate });
  const rates = useStayDailyRates();
  const swap = useSwapStayReservationCoverage();
  const contractPlan = useContractStayPlan();
  const [pendingTarget, setPendingTarget] = useState<StayCoverageTarget | null>(null);
  const [contractOpen, setContractOpen] = useState(false);
  const [kindFilter, setKindFilter] = useState<"recurring" | "day_package">("recurring");
  const current = coverage.data?.currentCoverage ?? { kind: "avulso" as const, contractId: null, label: "" };
  const dailyRate = module === "hotel" ? rates.data?.hotelNightlyRate ?? null : rates.data?.daycareDailyRate ?? null;
  const dailyItemName = module === "hotel" ? t("hotelNightService") : t("daycareDailyAdHoc");
  const currentCoverage = {
    kind: current.kind,
    contractId: current.contractId,
    name: current.label || t("planChoice.payAsYouGoOption"),
  };

  if (coverage.isLoading) {
    return <p className="text-sm text-muted-foreground">{t("panel.loading")}</p>;
  }

  if (coverage.isError) {
    return <p role="alert" className="text-sm text-destructive">{t("panel.coverageError")}</p>;
  }

  if (!coverage.data?.scheduledDays.length) {
    return <p className="text-sm text-muted-foreground">{t("panel.coverageNoFutureDays")}</p>;
  }

  const requestTarget = (target: StayCoverageTarget) => {
    if (target.kind === current.kind && target.contractId === current.contractId) return;
    setPendingTarget(target);
  };

  const handleContract = async (draft: StayPlanContractDraft) => {
    const result = await contractPlan.mutateAsync({ draft, petId, ownerId, deferInitialCharge: true });
    setContractOpen(false);
    requestTarget({
      kind: kindFilter === "day_package" ? "package" : "plan",
      contractId: result.contractId,
      label: result.planName,
    });
  };

  return (
    <section className="grid gap-3 rounded-xl border border-border bg-card p-4">
      <h3 className="text-sm font-semibold text-foreground">{t("panel.coverageTitle")}</h3>
      <StayCoverageChoice
        choice={current.kind === "avulso" ? "avulso" : "existing"}
        petId={petId}
        isLoading={options.isLoading}
        isError={options.isError}
        assignedContracts={options.assignedContracts}
        selectedAssignedId={current.contractId}
        currentCoverage={currentCoverage}
        disabled={swap.isPending}
        alerts={[]}
        usageSummary={null}
        reservationUnits={coverage.data.scheduledDays.length}
        weeklyReservationUnits={coverage.data.scheduledDays.length}
        onChange={({ choice, assignedId }) => {
          if (choice === "avulso") {
            requestTarget({ kind: "avulso", contractId: null, label: t("planChoice.payAsYouGoOption") });
            return;
          }
          const selected = options.assignedContracts.find((contract) => contract.contractId === assignedId);
          if (!selected) return;
          requestTarget({
            kind: selected.kind === "day_package" ? "package" : "plan",
            contractId: selected.contractId,
            label: selected.name,
          });
        }}
        onContract={(nextKind) => {
          setKindFilter(nextKind);
          setContractOpen(true);
        }}
      />
      <AlertDialog open={pendingTarget !== null} onOpenChange={(open) => { if (!open) setPendingTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("planChoice.coverageSwap.title")}</AlertDialogTitle>
            <AlertDialogDescription>{t("planChoice.coverageSwap.description", { days: coverage.data.scheduledDays.length, coverage: pendingTarget?.label ?? "", lines: coverage.data.days.filter((day) => day.dailyChargeSaleItemId).length })}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction disabled={swap.isPending} onClick={() => {
              if (!pendingTarget) return;
              void swap.mutateAsync({ reservationId, module, target: pendingTarget, dailyItemName, dailyRate })
                .then(() => setPendingTarget(null))
                .catch(() => undefined);
            }}>{t("createReservation")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {contractOpen ? (
        <StayPlanContractDialog
          open
          onOpenChange={setContractOpen}
          module={module}
          availableModules={[module]}
          kindFilter={kindFilter}
          defaultStartsOn={checkInDate}
          submitting={contractPlan.isPending}
          onSubmit={(draft) => void handleContract(draft)}
        />
      ) : null}
    </section>
  );
}
